// Performance monitoring and Core Web Vitals tracking
import { trackEvent } from './analytics';
import logger from './logger';

// Initialize performance tracking
export const initializePerformanceTracking = () => {
    // Track Core Web Vitals
    if ('web-vital' in window) {
        // This would require the web-vitals library
        // For now, we'll implement basic performance tracking
        trackBasicPerformance();
    } else {
        trackBasicPerformance();
    }

    // Track page load performance
    window.addEventListener('load', () => {
        setTimeout(() => {
            trackPageLoadPerformance();
        }, 1000);
    });

    logger.info('PERFORMANCE', 'Info');
};

// Basic performance tracking
const trackBasicPerformance = () => {
    // Navigation timing
    if (window.performance && window.performance.timing) {
        const timing = window.performance.timing;
        const loadTime = timing.loadEventEnd - timing.navigationStart;
        const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
        const firstPaint = timing.responseEnd - timing.navigationStart;

        trackEvent('page_performance', {
            load_time: loadTime,
            dom_content_loaded: domContentLoaded,
            first_paint: firstPaint,
            page_url: window.location.href,
            timestamp: new Date().toISOString()
        });
    }
};

// Track page load performance
const trackPageLoadPerformance = () => {
    if (window.performance && window.performance.getEntriesByType) {
        const navigationEntries = window.performance.getEntriesByType('navigation');
        const resourceEntries = window.performance.getEntriesByType('resource');

        if (navigationEntries.length > 0) {
            const nav = navigationEntries[0];
            trackEvent('page_load_performance', {
                dom_content_loaded: nav.domContentLoadedEventEnd - nav.domContentLoadedEventStart,
                load_complete: nav.loadEventEnd - nav.loadEventStart,
                first_byte: nav.responseStart - nav.requestStart,
                page_size: resourceEntries.reduce((total, resource) => total + (resource.transferSize || 0), 0),
                resource_count: resourceEntries.length,
                page_url: window.location.href,
                timestamp: new Date().toISOString()
            });
        }
    }
};

// Track API performance
export const trackAPIPerformance = (endpoint, method, startTime, endTime, success = true) => {
    const responseTime = endTime - startTime;
    
    trackEvent('api_performance', {
        endpoint,
        method,
        response_time: responseTime,
        success,
        timestamp: new Date().toISOString()
    });

    // Track slow API calls
    if (responseTime > 2000) { // 2 seconds
        trackEvent('slow_api_call', {
            endpoint,
            method,
            response_time: responseTime,
            timestamp: new Date().toISOString()
        });
    }
};

// Track media loading performance
export const trackMediaPerformance = (mediaType, mediaUrl, loadTime, success = true) => {
    trackEvent('media_performance', {
        media_type: mediaType, // 'video', 'audio', 'image'
        media_url: mediaUrl,
        load_time: loadTime,
        success,
        timestamp: new Date().toISOString()
    });

    // Track slow media loads
    if (loadTime > 5000) { // 5 seconds
        trackEvent('slow_media_load', {
            media_type: mediaType,
            media_url: mediaUrl,
            load_time: loadTime,
            timestamp: new Date().toISOString()
        });
    }
};

// Track user interaction performance
export const trackInteractionPerformance = (interactionType, responseTime, success = true) => {
    trackEvent('interaction_performance', {
        interaction_type: interactionType, // 'button_click', 'form_submit', 'navigation'
        response_time: responseTime,
        success,
        timestamp: new Date().toISOString()
    });

    // Track slow interactions
    if (responseTime > 1000) { // 1 second
        trackEvent('slow_interaction', {
            interaction_type: interactionType,
            response_time: responseTime,
            timestamp: new Date().toISOString()
        });
    }
};

// Track memory usage (if available)
export const trackMemoryUsage = () => {
    if (window.performance && window.performance.memory) {
        const memory = window.performance.memory;
        trackEvent('memory_usage', {
            used_js_heap_size: memory.usedJSHeapSize,
            total_js_heap_size: memory.totalJSHeapSize,
            js_heap_size_limit: memory.jsHeapSizeLimit,
            timestamp: new Date().toISOString()
        });
    }
};

// Track device performance
export const trackDevicePerformance = () => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    trackEvent('device_performance', {
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        cookie_enabled: navigator.cookieEnabled,
        on_line: navigator.onLine,
        connection_type: connection?.effectiveType || 'unknown',
        connection_downlink: connection?.downlink || 'unknown',
        connection_rtt: connection?.rtt || 'unknown',
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        color_depth: window.screen.colorDepth,
        timestamp: new Date().toISOString()
    });
};

export default {
    initializePerformanceTracking,
    trackAPIPerformance,
    trackMediaPerformance,
    trackInteractionPerformance,
    trackMemoryUsage,
    trackDevicePerformance
};
